import type { IBookingOptionSection } from '@booking-portal/modules/booking-flow/types/booking-flow-types';
import {
  guardNames,
  navigationNames,
} from '@booking-portal/router/router-constants';
import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router';

export const bookingFlowRouteNames = {
  continueFlow: 'booking-flow-continue',
  unitSelection: 'booking-flow-unit-type-selection',
  bookingOptions: 'booking-flow-options',
  unitTypeNotAvailable: 'booking-flow-unit-type-not-available',
  bookingBasket: 'booking-flow-basket',
  customerForm: 'booking-flow-customer-form',
  alreadyBooked: 'booking-flow-already-booked',
};

export const bookingFlowPageOrder = [
  bookingFlowRouteNames.unitSelection,
  bookingFlowRouteNames.bookingOptions,
  bookingFlowRouteNames.bookingBasket,
  bookingFlowRouteNames.customerForm,
];

const commonRouteMeta = {
  middleware: [guardNames.requiresLanguage],
};

export const bookingFlowRoutes: RouteRecordRaw[] = [
  // Standalone error page (no parent init) — deliberately sits outside the flow shell.
  {
    name: bookingFlowRouteNames.alreadyBooked,
    path: 'already-booked',
    component: () =>
      import('@booking-portal/modules/booking-flow/pages/BookingFlowAlreadyBookedErrorPage.vue'),
  },
  {
    path: '',
    component: () =>
      import('@booking-portal/modules/booking-flow/pages/BookingFlowPage.vue'),
    meta: {
      ...commonRouteMeta,
    },
    children: [
      {
        name: bookingFlowRouteNames.unitSelection,
        path: '',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowUnitTypeSelectionPage.vue'),
      },
      // Continue is a child: the parent's init handles the restore + redirect,
      // and this page just shows a skeleton until that finishes.
      {
        name: bookingFlowRouteNames.continueFlow,
        path: 'continue',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowContinuePage.vue'),
      },
      {
        path: 'unit-type/:unitTypeId(\\d+)',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowUnitTypePreSelectPage.vue'),
      },
      {
        name: bookingFlowRouteNames.bookingOptions,
        path: 'unit-type/:unitTypeId(\\d+)/options',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowOptionsPage.vue'),
        children: [
          {
            name: bookingFlowRouteNames.bookingBasket,
            path: 'basket',
            component: () =>
              import('@booking-portal/modules/booking-flow/pages/BookingFlowBasketPage.vue'),
          },
        ],
      },
      {
        name: bookingFlowRouteNames.unitTypeNotAvailable,
        path: 'unit-type/:unitTypeId(\\d+)/options/not-available/:moveInDate(\\d{4}-\\d{2}-\\d{2})',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowUnitTypeNotAvailablePage.vue'),
      },
      {
        name: bookingFlowRouteNames.customerForm,
        path: 'unit-type/:unitTypeId(\\d+)/customer',
        component: () =>
          import('@booking-portal/modules/booking-flow/pages/BookingFlowCustomerFormPage.vue'),
      },
    ],
  },
];

export const routesFactoryBookingFlow = {
  goToBookingFlow(locationId?: number, unitTypeId?: number) {
    if (!locationId) {
      return {
        name: navigationNames.notFound,
      };
    }

    if (!unitTypeId) {
      return routesFactoryBookingFlow.unitTypeSelection();
    }

    return routesFactoryBookingFlow.customerForm(unitTypeId);
  },
  unitTypeSelection(unitTypeCategoryId?: number) {
    const route: RouteLocationRaw = {
      name: bookingFlowRouteNames.unitSelection,
    };

    if (unitTypeCategoryId) {
      route.query = {
        unitTypeCategoryId: unitTypeCategoryId,
      };
    }

    return route;
  },
  continueFlow() {
    return {
      name: bookingFlowRouteNames.continueFlow,
    };
  },
  customerForm(unitTypeId: number | undefined) {
    return {
      name: bookingFlowRouteNames.customerForm,
      params: {
        unitTypeId,
      },
    };
  },
  bookingOptions(
    unitTypeId: number | undefined,
    section?: IBookingOptionSection | null
  ) {
    return {
      name: bookingFlowRouteNames.bookingOptions,
      query: {
        section,
      },
      params: {
        unitTypeId,
      },
    };
  },
  bookingBasket(unitTypeId: number | undefined) {
    return {
      name: bookingFlowRouteNames.bookingBasket,
      query: {},
      params: {
        unitTypeId,
      },
    };
  },
  unitTypeNotAvailable(unitTypeId: number | undefined, moveInDate: string) {
    return {
      name: bookingFlowRouteNames.unitTypeNotAvailable,
      params: {
        moveInDate,
        unitTypeId,
      },
    };
  },
  alreadyBooked(locationId: number | undefined) {
    return {
      name: bookingFlowRouteNames.alreadyBooked,
      params: {
        locationId,
      },
    };
  },
};
